// ---------------------------------------------------------------------------
// ConfirmDelete — inline confirmation bar before a destructive schema/data edit.
//
// Wave-7 carryover. Two defects, both on a DESTRUCTIVE control:
//
// 1. ENGLISH. The prompt and both buttons were hardcoded English in a
//    five-locale product, and the prompt was assembled in JSX —
//    `Delete <span>{entityName}</span>?` — so no locale could own its own word
//    order or the position of the identifier inside the sentence. Each locale
//    now authors ONE sentence per variant and receives the identifier as a
//    parameter.
//
// 2. A FALSE SENTENCE. `schema-field-row.tsx` reused this component to confirm
//    a schema TYPE CHANGE by stuffing an English clause into the identifier
//    slot — `entityName={`type change for "${field.name}"`}`. Under `ko` that
//    would have rendered `"type change for "amount""을(를) 삭제할까요?`: a
//    foreign fragment inside a Korean sentence (the class this product already
//    shipped once as `이 staging 배포를 내릴까요?`), AND the word "delete" for
//    an operation that is not a delete. The `variant` prop below is the repair.
//    The guard is deliberately NOT softened: dropping nested fields IS
//    destructive and still confirms — only the wording is made true.
//
// The identifier is interpolated, never authored: entity/variable/field names
// are project payload that the operator matches against the JSON and against
// what they typed.
// ---------------------------------------------------------------------------

import { useStudioLocale } from '@/i18n'
import type { StudioDictionary } from '@/i18n'

type ConfirmCopy = StudioDictionary['dataValidation']['confirm']

/**
 * Which destructive operation is being confirmed.
 *
 * Adding a member here is a COMPILE ERROR until `VARIANT_COPY` gains a branch
 * that reads it — that record is `Record<ConfirmDeleteVariant, …>`, and it is
 * the only place the prompt/action pair is chosen. `CONFIRM_DELETE_VARIANTS`
 * exports the record's own keys so the gate enumerates what the component
 * actually reads rather than a hand-written list that can drift out of date.
 */
export type ConfirmDeleteVariant = 'delete' | 'typeChange'

const VARIANT_COPY: Record<
  ConfirmDeleteVariant,
  (copy: ConfirmCopy) => { prompt: (name: string) => string; action: string }
> = {
  delete: (copy) => ({
    prompt: copy.deletePrompt,
    action: copy.deleteAction,
  }),
  typeChange: (copy) => ({
    prompt: copy.typeChangePrompt,
    action: copy.typeChangeAction,
  }),
}

/** The variants the component actually branches on — derived, not hand-listed. */
export const CONFIRM_DELETE_VARIANTS = Object.keys(
  VARIANT_COPY,
) as ConfirmDeleteVariant[]

interface Props {
  /**
   * The identifier being acted on — an entity or schema-field name. Rendered
   * verbatim inside the localized sentence; never translated or case-folded.
   */
  entityName: string
  onConfirm: () => void
  onCancel: () => void
  /**
   * Extra consequence line. ALREADY LOCALIZED by the caller — this component
   * cannot localize it, because only the caller knows what the consequence is.
   */
  warning?: string
  /** Defaults to `delete`, which is what every pre-existing caller meant. */
  variant?: ConfirmDeleteVariant
}

export function ConfirmDelete({
  entityName,
  onConfirm,
  onCancel,
  warning,
  variant = 'delete',
}: Props) {
  const { strings } = useStudioLocale()
  const copy = strings.dataValidation.confirm
  const { prompt, action } = VARIANT_COPY[variant](copy)

  return (
    <div className="px-3 py-2 bg-destructive/10 border-y border-destructive/20 space-y-1.5">
      {/*
        One authored sentence. The identifier is quoted by the locale itself
        (the convention every identifier-bearing sentence in this dictionary
        already uses, e.g. `변수 "title"을(를) 삭제했습니다`), which replaces the
        mono <span> the JSX-assembled version used to distinguish it.
      */}
      <p className="text-xs text-destructive">{prompt(entityName)}</p>
      {warning && (
        <p className="text-[10px] text-destructive/80">{warning}</p>
      )}
      <div className="flex gap-2">
        {/*
          Test ids are locale- AND variant-independent on purpose:
          `e2e/studio/studio-core-editor-task-success.spec.ts` and
          `e2e/studio/sipass-admin-data.spec.ts` both drive this bar through
          `confirm-delete-button`.
        */}
        <button
          data-testid="confirm-delete-button"
          onClick={onConfirm}
          className="px-3 py-1 rounded text-[10px] font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
        >
          {action}
        </button>
        <button
          data-testid="confirm-delete-cancel"
          onClick={onCancel}
          className="px-3 py-1 rounded text-[10px] text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          {copy.cancel}
        </button>
      </div>
    </div>
  )
}
